import React, { useState } from 'react';
import { 
  FaEnvelope, 
  FaUser, 
  FaBuilding, 
  FaMobile, 
  FaTag, 
  FaLink, 
  FaComment, 
  FaPaperPlane,
  FaCheck,
  FaTimes
} from 'react-icons/fa';
import Header from '../components/Header';

const PlayStoreContact = () => {
  const [formData, setFormData] = useState({
    developerEmail: '',
    developerName: '',
    companyName: '',
    appName: '',
    appPackageName: '',
    appCategory: '',
    appDescription: '',
    playStoreUrl: '',
    contactReason: 'collaboration',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/playstore-emails', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(true);
        setFormData({
          developerEmail: '',
          developerName: '',
          companyName: '',
          appName: '',
          appPackageName: '',
          appCategory: '',
          appDescription: '',
          playStoreUrl: '',
          contactReason: 'collaboration',
          message: ''
        });
      } else {
        setError(result.message || 'An error occurred');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const appCategories = [
    'Games',
    'Entertainment',
    'Education',
    'Business',
    'Productivity',
    'Lifestyle',
    'Health & Fitness',
    'Music & Audio',
    'Photography',
    'Shopping',
    'Social',
    'Travel & Local',
    'News & Magazines',
    'Books & Reference',
    'Finance',
    'Food & Drink',
    'Sports',
    'Weather',
    'Other'
  ];

  const contactReasons = [
    { value: 'collaboration', label: 'Collaboration' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'support', label: 'Support' },
    { value: 'feedback', label: 'Feedback' },
    { value: 'other', label: 'Other' }
  ];

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-purple-900/40 to-indigo-900/50">
        <Header />
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="max-w-md w-full bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaCheck className="text-white text-2xl" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Thank You!</h2>
            <p className="text-white/80 mb-6">
              Your contact request has been submitted successfully. We'll get back to you soon.
            </p>
            <button
              onClick={() => setSuccess(false)}
              className="px-6 py-3 bg-white text-black rounded-lg hover:bg-white/90 transition-colors font-medium"
            >
              Submit Another Request
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-purple-900/40 to-indigo-900/50">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            Contact AppActs Team
          </h1>
          <p className="text-xl text-white/80">
            Are you a Play Store developer? Let's collaborate!
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Developer Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  <FaUser className="inline mr-2" />
                  Developer Name *
                </label>
                <input
                  type="text"
                  name="developerName"
                  value={formData.developerName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  <FaEnvelope className="inline mr-2" />
                  Email Address *
                </label>
                <input
                  type="email"
                  name="developerEmail"
                  value={formData.developerEmail}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                <FaBuilding className="inline mr-2" />
                Company Name
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your company name (optional)"
              />
            </div>

            {/* App Information */}
            <div className="border-t border-white/20 pt-6">
              <h3 className="text-lg font-semibold text-white mb-4">App Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    <FaMobile className="inline mr-2" />
                    App Name *
                  </label>
                  <input
                    type="text"
                    name="appName"
                    value={formData.appName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your app name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    <FaTag className="inline mr-2" />
                    Package Name *
                  </label>
                  <input
                    type="text"
                    name="appPackageName"
                    value={formData.appPackageName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="com.yourcompany.yourapp"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    <FaTag className="inline mr-2" />
                    App Category *
                  </label>
                  <select
                    name="appCategory"
                    value={formData.appCategory}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select category</option>
                    {appCategories.map(category => (
                      <option key={category} value={category} className="bg-gray-800">
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    <FaLink className="inline mr-2" />
                    Play Store URL
                  </label>
                  <input
                    type="url"
                    name="playStoreUrl"
                    value={formData.playStoreUrl}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://play.google.com/store/apps/details?id=..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  App Description
                </label>
                <textarea
                  name="appDescription"
                  value={formData.appDescription}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief description of your app..."
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="border-t border-white/20 pt-6">
              <h3 className="text-lg font-semibold text-white mb-4">Contact Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Contact Reason *
                  </label>
                  <select
                    name="contactReason"
                    value={formData.contactReason}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {contactReasons.map(reason => (
                      <option key={reason.value} value={reason.value} className="bg-gray-800">
                        {reason.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  <FaComment className="inline mr-2" />
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Tell us more about your collaboration idea..."
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-center">
                <FaTimes className="text-red-400 mr-2" />
                <span className="text-red-200">{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center mx-auto"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <FaPaperPlane className="mr-2" />
                    Submit Contact Request
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Benefits Section */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaUser className="text-white text-xl" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Developer Network</h3>
            <p className="text-white/70">Connect with other developers and share experiences</p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaBuilding className="text-white text-xl" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Partnership Opportunities</h3>
            <p className="text-white/70">Explore collaboration and partnership possibilities</p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaMobile className="text-white text-xl" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">App Promotion</h3>
            <p className="text-white/70">Get help promoting your apps and reaching more users</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayStoreContact;
